import { LiveChatWidget } from '@livechat/widget-react';
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
// @mui
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  capitalize,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ReactPixel from 'react-facebook-pixel';

// hooks
import { useSnackbar } from 'notistack';
import useSettings from '../../hooks/useSettings';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// config
import { HEADER, NAVBAR } from '../../config';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { openLogin, openRegister } from '../../redux/slices/authUiSlice';
import useAuth from '../../hooks/useAuth';

import AccountInfo from '../../views/user/AccountInfo';

import DashboardHeader from './header';

import { getGameCategsAndCompanies, getSiteSettings, getUserMemberBalance } from '../../redux/slices/lookup';

import useLocales from '../../hooks/useLocales';

import { setGameCategories, setAllGames, setActiveTab } from '../../redux/slices/game';

import conObj from '../../utils/connection-assistant';

import { usePageVisibility } from '../../hooks/usePageVisibility';
import { closeLoadingScreen, openLoadingScreen, setShowLiveChat } from '../../redux/slices/settings';

import Image from '../../components/Image';
import Close from '../../assets/home/close.png';
import { BottomDrawer } from '../../components/bottom-drawer/BottomDrawer';
import Instruction from '../../components/instruction/instruction';
import { MyFooter } from '../../components/footer/MyFooter';
import { BannerHeader } from '../../components/banner-header/BannerHeader';
import { Announcement } from '../../components/announcement/Announcement';
import useResponsive from '../../hooks/useResponsive';

import Home from '../../assets/navigation/home-classic.png';
import HomeHover from '../../assets/navigation/home-hover.png';
import Settings from '../../assets/navigation/settings.png';
import SettingsHover from '../../assets/navigation/settings-hover.png';
import Clock from '../../assets/navigation/clock.png';
import AiwinImageBanner from '../../assets/aiwin_casino.jpg';
import HistoryBanner from '../../assets/history/historyBanner.jpg';
import ClockHover from '../../assets/navigation/clock-hover.png';
import Gift from '../../assets/navigation/gift-classic.png';
import GiftHover from '../../assets/navigation/gift-hover.png';
import Deposit from '../../assets/navigation/deposit.png';
import DepositHover from '../../assets/navigation/deposit-hover.png';
import LoginForm from './header/visitor/LoginForm';
import { fNumberDecimal } from '../../utils/formatNumber';
import PopupBanner from './PopupBanner';
import BannerCarousel from '../../views/game/BannerCarousel';
import WithdrawFormDialog from '../../views/withdraw/WithdrawFormDialog';

// ----------------------------------------------------------------------

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ collapseClick, theme }) => ({
  flexGrow: 1,
  height: '100vh',
  backgroundColor: 'black',
  minHeight: '100vh',
  paddingBottom: HEADER.MOBILE_HEIGHT,
  [theme.breakpoints.up('lg')]: {
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

const DashboardLayout = () => {
  const theme = useTheme();

  const isDesktop = useResponsive('up', 'lg');

  const location = useLocation();

  const dispatchUi = useDispatch();

  const isActive = (path) => location.pathname.startsWith(path);

  const { isAuthenticated, userProfile, logout } = useAuth();

  const { collapseClick } = useCollapseDrawer();

  const { isAddMemberBankDialog } = useSelector((x) => x.addMemberBankDialog);

  const { siteSettings, showLiveChat, memberBalance } = useSelector((x) => ({ ...x.lookup, ...x.settings }));

  const [openProfile, setOpenProfile] = useState(false);

  const isVisible = usePageVisibility();

  const { translate } = useLocales();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const isArrangement = ['games'].some((path) => location.pathname.includes(path));

  const isHistory = ['history', 'user', 'referral', 'leaderboard'].some((path) => location.pathname.includes(path));

  const isHome = location.pathname === '/';

  const { themeLayout } = useSettings();

  const [menuIndex, setMenuIndex] = useState(1);

  const intervalRef = useRef(null);

  const verticalLayout = themeLayout === 'vertical';

  const { enqueueSnackbar } = useSnackbar();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  useEffect(() => {
    // const promises = [// getAllGames(), 
    //   getGameCategsAndCompanies()];

    if (localStorage.getItem('referralCode')) {
      navigate('/');
      dispatchUi(openRegister());
    }

    // getSiteSettings();

    // dispatch(openLoadingScreen());
    // Promise.all(promises).finally(() => dispatch(closeLoadingScreen()));
  }, []);

  /**
   * Call getUserMemberBalance() when tab is active and isAuthenticated
   * to keep the member balance updated
   */

  useEffect(() => {
    if (isVisible && isAuthenticated) {
      intervalRef.current = setInterval(() => {
        getUserMemberBalance();
      }, 30000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isVisible, isAuthenticated]);

  // useEffect(() => {
  //   const urlParams = new URLSearchParams(location.search);

  //   const aff = urlParams.get('aff');

  //   if (aff) {
  //     dispatchUi(openRegister());
  //   }
  // }, [location.search, dispatch]);

  useEffect(() => {
    try {
      if (siteSettings?.config_analytics) {
        const _ga = siteSettings?.config_analytics?.split(',');

        // Multiple products (previously known as trackers)
        ReactGA.initialize(
          _ga.map((item) => ({
            // measurement ID
            trackingId: item,
            // gaOptions: {...}, // optional
            // gtagOptions: {...}, // optional
          }))
          //   [
          //   {
          //     // measurement ID
          //     trackingId: _ga[0],
          //     // gaOptions: {...}, // optional
          //     // gtagOptions: {...}, // optional
          //   },
          // ]
        );
      }

      if (siteSettings?.config_fb_pixel) {
        const metaPixel = siteSettings.config_fb_pixel.replace(/[^\d,]+/g, '').split(',');
        const options = {
          autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
          debug: false, // enable logs
        };

        metaPixel.forEach((_pixel) => {
          ReactPixel.init(_pixel, options);

          ReactPixel.pageView(); // For tracking page view
        });
      }

      // if (siteSettings?.config_livechat) {
      //   const respondIo = JSON.parse(siteSettings?.config_livechat);
      //   const existingScript = document.getElementById(respondIo?.id);

      //   if (!existingScript) {
      //     const script = document.createElement('script');
      //     script.id = respondIo?.id;
      //     script.src = respondIo?.src;
      //     script.async = true;

      //     document.head.appendChild(script);
      //   }
      // }

      // if (siteSettings?.config_livechat) {
      //   const respondIo = JSON.parse(siteSettings?.config_livechat);
      //   const existingScript = document.getElementById(respondIo?.id);

      //   if (!existingScript) {
      //     const script = document.createElement('script');
      //     script.id = respondIo?.id;
      //     script.src = respondIo?.src;
      //     script.async = true;

      //     document.head.appendChild(script);
      //   }
      // }
    } catch (error) {
      console.warn(`3RD PARTY INTEGRATION ERROR: ${error?.message}`);
    }
  }, [siteSettings]);

  const getAllGames = () => {
    conObj.post('games/all.json?limit=1').then((res) => {
      if (res.status === 200) {
        // res.data?.data?.data
        // set appversion to localstorage
        // res.data?.data.appversion
        const _allGames = res.data?.data?.data;
        dispatch(setAllGames(_allGames));
        dispatch(
          setGameCategories(
            _allGames
              .filter((f) => f.key.toLowerCase().indexOf('gcat') !== -1)
              .map((item) => ({ key: item.key, name: item.name, slug: item.slug }))
          )
        );
      }
    });
  };

  const [openSettings, setOpenSettings] = useState(false);

  const handleOpen = () => {
    setOpenSettings(!openSettings);
  };

  const handleClose = () => {
    setOpenSettings(false);
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  if (verticalLayout) {
    return (
      <>
        <Box
          component="main"
          sx={{
            backgroundColor: 'black',
            pt: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 80}px`,
            },
            pb: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px`,
            },
          }}
        >
          <Outlet />
        </Box>
      </>
    );
  }

  const bottomNavItems = [
    {
      label: 'HOME',
      path: '/',
      icon: Home,
      activeIcon: HomeHover,
      onClick: () => navigate('/'),
    },
    {
      label: 'HISTORY',
      path: '/history',
      icon: Clock,
      activeIcon: ClockHover,
      onClick: () => {
        if (isAuthenticated) navigate('/history?tab=transaction');
        else dispatchUi(openLogin());
      },
    },
    {
      label: 'PROMOTIONS',
      path: '/promotion',
      icon: Gift,
      activeIcon: GiftHover,
      onClick: () => navigate('/promotion/tab'),
    },
    {
      label: 'DEPOSIT',
      path: '/user/wallet',
      icon: Deposit,
      activeIcon: DepositHover,
      onClick: () => {
        if (isAuthenticated) navigate('/user/wallet');
        else dispatchUi(openLogin());
      },
    },
    {
      label: 'SETTINGS',
      path: '/settings',
      icon: Settings,
      activeIcon: SettingsHover,
      onClick: () => {
        if (isAuthenticated) setDrawerOpen(true);
        else dispatchUi(openLogin());
      },
    },
  ];

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: theme.palette.goldcity.background.main,
        position: 'relative',
        pt: { xs: '14vw', lg: 0 },
      }}
    >
      <DashboardHeader />

      {isDesktop && <BannerHeader />}

      <Announcement />
      <PopupBanner />

      {isHome && <BannerCarousel />}

      {isArrangement && (
        <Image
          src={AiwinImageBanner}
          sx={{
            width: '100%',
            height: { xs: '100px', md: 'auto' },
          }}
        />
      )}

      {isHistory && (
        <Image
          src={HistoryBanner}
          sx={{
            width: '100%',
            aspectRatio: { xs: '12/5', md: '10/2' },
            objectFit: 'cover',
          }}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.goldcity.background.main,
        }}
      >
        <Box
          sx={{
            maxWidth: '1250px',
            width: '100%',
          }}
        >
          <Outlet />
        </Box>
      </Box>
      {/* {!isMobile && <MyFooter />} */}

      {/* {!isDesktop && (
        <BottomNavigation
          value={value}
          onChange={(event, newValue) => {
            setValue(newValue);
            navigate(bottomNavigation[newValue].path);
          }}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            paddingX: '4px',
            height: '75px',
            backgroundColor: '#1a212eff',
          }}
        >
          {bottomNavigation.map((items, index) => (
            <BottomNavigationAction
              disableRipple
              key={index}
              label={capitalize(items.title)}
              icon={<IconComponent icon={value === index ? items.active : items.icon} />}
              sx={{
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '8.47px',
                  color: value === index ? '#F49825' : 'white',
                  mt: '4px',
                },
                '& .MuiBottomNavigationAction-root.Mui-selected': {
                  transform: 'none',
                },
              }}
            />
          ))}
        </BottomNavigation>
      )} */}
      {!isDesktop && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            paddingX: '4px',
            height: '75px',
            zIndex: 100,
            background: 'linear-gradient(180.0deg, #303030 0%, #101010 100%)',
            borderTop: '1px solid #232323',
            boxShadow: '0 -2px 8px 0 rgba(0,0,0,0.4)',
          }}
        >
          <Box display="flex" justifyContent="space-between" py={1} px={2}>
            {bottomNavItems.map((item, index) => {
              const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

              return (
                <Box
                  key={index}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={0.5}
                  sx={{ cursor: 'pointer', width: 'fit-content' }}
                  onClick={item.onClick}
                >
                  {active ? (
                    <Image src={item.activeIcon} sx={{ width: '32px', height: '32px' }} />
                  ) : (
                    <Image src={item.icon} sx={{ width: '32px', height: '32px' }} />
                  )}

                  <Typography color={active ? '#F9C83D' : 'white'} fontSize="10px">
                    {translate(item.label)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {siteSettings?.config_livechat && siteSettings?.config_livechat?.length === 8 ? (
        <LiveChatWidget
          license={siteSettings?.config_livechat}
          visibility={showLiveChat ? 'maximized' : 'minimized'}
          onReady={(data) => {
            const widgetContainer = document.getElementById('chat-widget-container');

            if (widgetContainer && data.state.visibility === 'minimized') {
              widgetContainer.classList.add('adjust-bottom-space');
            }
          }}
          onVisibilityChanged={(data) => {
            const widgetContainer = document.getElementById('chat-widget-container');

            if (widgetContainer) {
              if (data.visibility === 'maximized') {
                dispatch(setShowLiveChat(true));
                widgetContainer.classList.remove('adjust-bottom-space');
              } else {
                widgetContainer.classList.add('adjust-bottom-space');
                dispatch(setShowLiveChat(false));
              }
            }
          }}
        />
      ) : (
        <></>
      )}

      {isAuthenticated && <AccountInfo open={drawerOpen} handleClose={toggleDrawer(false)} />}
      {isAuthenticated && <WithdrawFormDialog />}
      {/* {isAuthenticated && (
        <Box>
          <SwipeableDrawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)} onOpen={toggleDrawer(true)}>
            <Box width={250} role="presentation">
              <List>
                <ListItemButton>
                  <ListItemText primary={userProfile?.full_name} />
                </ListItemButton>
              </List>

              <Divider />

              <List>
                <ListItemButton>
                  <ListItemText
                    primary={`${memberBalance?.currencyCode} ${
                      memberBalance?.displayBalance ? fNumberDecimal(memberBalance?.displayBalance) : 0
                    }`}
                  />
                </ListItemButton>
              </List>

              <Divider />

              <List>
                <ListItemButton
                  onClick={() => {
                    setOpenProfile(true);
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemText primary={translate('account')} />
                </ListItemButton>

                <ListItemButton
                  onClick={() => {
                    navigate('/user/account');
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemText primary={translate('bank_account')} />
                </ListItemButton>

                <ListItemButton
                  onClick={() => {
                    navigate('/referral');
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemText primary={translate('referral')} />
                </ListItemButton>

                <ListItemButton
                  onClick={() => {
                    navigate('/history/tab');
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemText primary={translate('history')} />
                </ListItemButton>

                <ListItemButton
                  onClick={() => {
                    handleLogout();
                    navigate('/');
                    setDrawerOpen(false);
                  }}
                >
                  <ListItemText primary={translate('logout')} />
                </ListItemButton>
              </List>
            </Box>
          </SwipeableDrawer>
        </Box>
      )} */}
    </Box>
  );
};

export default DashboardLayout;
